import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Upload, Image as ImageIcon, ChevronRight, AlertCircle, X, Package, Info } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { StockItemQuantitiesTab } from "@/components/stock-item-quantities-tab";
import { StockItemPricingTab } from "@/components/stock-item-pricing-tab";
import { StockItemAnalysisTab } from "@/components/stock-item-analysis-tab";
import { StockItemSupplierTab } from "@/components/stock-item-supplier-tab";
import { StockItemSettingsTab } from "@/components/stock-item-settings-tab";
import { StockItemIntrastatTab } from "@/components/stock-item-intrastat-tab";
import { calculateCubicVolume, formatCubicVolume } from "@/lib/cubic-volume";
import { useScreenMode } from "@/providers/ModeProvider";
import { useGraphQLFetch } from "@/hooks/use-graphql-fetch";

// GraphQL mutation for creating stock items
const CREATE_STOCK_ITEM_MUTATION = `
  mutation CreateStockItem($stockItem: StockItemInput!) {
    stockItems {
      stockItemsRecords {
        create(stockItem: $stockItem) {
          __typename
          ... on IntEntityId {
            id
          }
        }
      }
    }
  }
`;

// Map frontend StockItem to backend StockItemInput
const mapToBackendInput = (item: StockItem) => ({
  stockCode: item.code,
  description: item.description,
  category: item.category || null,
  location: item.location || null,
  warehouseCode: item.warehouseCode || null,
  locationCode: item.location || null,
  barcode: item.barcode || null,
  imageURL: item.image || null,
  physicalQuantity: item.physicalQuantity,
  allocatedQuantity: item.allocatedQuantity,
  quantityDueIn: item.quantityDueIn,
  minQuantity: item.minQuantity,
  maxQuantity: item.maxQuantity,
  reorderPoint: item.reorderPoint,
  reorderQuantity: item.reorderQuantity,
  unitPrice: item.unitPrice,
  lastCostPrice: item.lastCostPrice || 0,
  averageCost: item.averageCost || 0,
  totalValue: item.value || 0,
  weight: item.weight || null,
  weightUnit: item.weightUnit || null,
  binNumber: item.binNumber || null,
  // Unit of Measure and Packaging
  unitOfMeasure: item.unitOfMeasure || "Each",
  packSize: item.packSize || null,
  cartonQuantity: item.cartonQuantity || null,
  weightPerUnit: item.weightPerUnit || null,
  weightPerUnitUnit: item.weightPerUnitUnit || null,
  dimensionLength: item.dimensionLength || null,
  dimensionWidth: item.dimensionWidth || null,
  dimensionHeight: item.dimensionHeight || null,
  dimensionUnit: item.dimensionUnit || null,
  cubicVolume: item.cubicVolume || null,
  purchaseAnalysisCode: item.purchaseAnalysisCode || null,
  salesAnalysisCode: item.salesAnalysisCode || null,
  movementInAnalysis: item.movementInAnalysis || null,
  movementOutAnalysis: item.movementOutAnalysis || null,
  aBCClassification: item.abcClassification || null,
  supplier: item.supplier || null,
  leadTimeDays: item.leadTimeDays || null,
  inactive: item.inactive,
});

interface StockItem {
  code: string;
  description: string;
  category: string;
  location: string;
  warehouseCode?: string;
  locationCode?: string;
  physicalQuantity: number;
  unitPrice: number;
  value: number;
  inactive: boolean;
  lastMovementDate: Date;
  barcode: string;
  image?: string;
  weight?: number;
  weightUnit?: "kg" | "g" | "lbs";
  binNumber?: string;
  allocatedQuantity: number;
  quantityDueIn: number;
  minQuantity: number;
  maxQuantity: number;
  reorderPoint: number;
  reorderQuantity: number;
  lastCostPrice: number;
  averageCost: number;
  totalCostOfSales: number;
  totalValueOfSales: number;
  purchaseAnalysisCode?: string;
  salesAnalysisCode?: string;
  movementInAnalysis?: string;
  movementOutAnalysis?: string;
  abcClassification: "A" | "B" | "C";
  stockTurnRate: number;
  supplier?: string;
  leadTimeDays?: number;
  lastPurchaseDate?: Date | null;
  lastSaleDate?: Date | null;
  supplierContact?: string;
  lastPurchaseQuantity?: number;
  lastPurchasePrice?: number;
  lastSaleQuantity?: number;
  lastSalePrice?: number;
  onTimeDeliveryRate?: number;
  averageLeadTime?: number;
  totalOrdersYTD?: number;
  // Unit of Measure and Packaging fields
  unitOfMeasure: "Each" | "Box/Carton" | "Kg" | "g" | "Litre" | "Metre" | "Pallet";
  packSize?: number;
  cartonQuantity?: number;
  weightPerUnit?: number;
  weightPerUnitUnit?: "kg" | "g" | "lbs";
  dimensionLength?: number;
  dimensionWidth?: number;
  dimensionHeight?: number;
  dimensionUnit?: "cm" | "m" | "in" | "ft";
  cubicVolume?: number;
  // Settings fields
  isSellable?: boolean;
  isPurchasable?: boolean;
  isAssembly?: boolean;
  isComponent?: boolean;
  allowDisassembly?: boolean;
  trackingType?: "None" | "Lot" | "Batch" | "Serial";
  autoGenerateSerial?: boolean;
  serialPrefix?: string;
  requireExpiryDate?: boolean;
  requireManufacturingDate?: boolean;
  shelfLifeDays?: number;
  allowNegativeStock?: boolean;
  reorderLevel?: number;
  // Type field - combines tracking and procurement
  type?: 
    | "Purchased" 
    | "Manufactured" 
    | "Serial Number" 
    | "Assembly" 
    | "Component" 
    | "Template";
}

interface ValidationErrors {
  [key: string]: string;
}

interface CreateStockItemPageProps {
  onNavigate: (page: string) => void;
  existingStockItems?: StockItem[];
  onItemCreated?: (item: StockItem) => void;
}

export function CreateStockItemPage({ onNavigate, existingStockItems = [], onItemCreated }: CreateStockItemPageProps) {
  const [activeTab, setActiveTab] = useState("details");
  const [isDragging, setIsDragging] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [isPackagingOpen, setIsPackagingOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
    // Mode context for demo/live toggle (screen-specific)
    const { setMode, isDemo, isLive } = useScreenMode('create-stock-item');
    const { fetchGraphQL } = useGraphQLFetch();

  // Initialize new stock item with defaults
  const [newItem, setNewItem] = useState<StockItem>({
    code: "",
    description: "",
    category: "",
    location: "",
    physicalQuantity: 0,
    unitPrice: 0,
    value: 0,
    inactive: false,
    lastMovementDate: new Date(),
    barcode: "",
    image: undefined,
    weight: undefined,
    weightUnit: "kg",
    binNumber: "",
    allocatedQuantity: 0,
    quantityDueIn: 0,
    minQuantity: 0,
    maxQuantity: 0,
    reorderPoint: 0,
    reorderQuantity: 0,
    lastCostPrice: 0,
    averageCost: 0,
    totalCostOfSales: 0,
    totalValueOfSales: 0,
    purchaseAnalysisCode: "",
    salesAnalysisCode: "",
    movementInAnalysis: "",
    movementOutAnalysis: "",
    abcClassification: "C",
    stockTurnRate: 0,
    supplier: "",
    leadTimeDays: undefined,
    lastPurchaseDate: null,
    lastSaleDate: null,
    supplierContact: "",
    // UOM and Packaging defaults
    unitOfMeasure: "Each",
    packSize: undefined,
    cartonQuantity: undefined,
    weightPerUnit: undefined,
    weightPerUnitUnit: "kg",
    dimensionLength: undefined,
    dimensionWidth: undefined,
    dimensionHeight: undefined,
    dimensionUnit: "cm",
    cubicVolume: undefined,
    // Settings defaults
    isSellable: true,
    isPurchasable: true,
    isAssembly: false,
    isComponent: true,
    allowDisassembly: false,
    trackingType: "None",
    autoGenerateSerial: false,
    serialPrefix: "",
    requireExpiryDate: false,
    requireManufacturingDate: false,
    shelfLifeDays: undefined,
    allowNegativeStock: false,
    reorderLevel: undefined,
    // Type default
    type: "Purchased",
  });



  // Check for draft on mount
  useEffect(() => {
    const draft = localStorage.getItem("stockItemDraft");
    if (draft) {
      try {
        const parsedDraft = JSON.parse(draft);
        // Show restore dialog
        const shouldRestore = window.confirm("You have an unsaved draft. Would you like to restore it?");
        if (shouldRestore) {
          setNewItem(parsedDraft);
          setHasUnsavedChanges(true);
          toast.info("Draft restored");
        } else {
          localStorage.removeItem("stockItemDraft");
        }
      } catch (error) {
        console.error("Failed to parse draft:", error);
        localStorage.removeItem("stockItemDraft");
      }
    }
  }, []);

  // Auto-save draft every 30 seconds
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const interval = setInterval(() => {
      localStorage.setItem("stockItemDraft", JSON.stringify(newItem));
    }, 30000);

    return () => clearInterval(interval);
  }, [newItem, hasUnsavedChanges]);

  // Validate field
  const validateField = (field: string, value: any): string => {
    switch (field) {
      case "code":
        if (!value || value.trim() === "") return "Stock code is required";
        if (value.length > 25) return "Stock code must be 25 characters or less";
        if (existingStockItems.some(item => item.code.toLowerCase() === value.toLowerCase())) {
          return "This code already exists";
        }
        return "";
      
      case "description":
        if (!value || value.trim() === "") return "Description is required";
        if (value.length < 3) return "Description must be at least 3 characters";
        if (value.length > 200) return "Description must be 200 characters or less";
        return "";
      
      case "category":
        // Category is now optional
        return "";
      
      case "type":
        if (!value || value === "") return "Type is required";
        return "";
      
      case "unitPrice":
        if (value !== undefined && value !== null && value < 0) return "Unit price cannot be negative";
        return "";
      
      case "physicalQuantity":
        if (value !== undefined && value !== null && value < 0) return "Physical quantity cannot be negative";
        return "";
      
      case "barcode":
        if (value && (value.length < 8 || value.length > 13)) {
          return "Barcode must be 8-13 digits";
        }
        if (value && !/^\d+$/.test(value)) {
          return "Barcode must contain only digits";
        }
        return "";
      
      case "maxQuantity":
        if (value && value <= newItem.minQuantity) {
          return "Max quantity must be greater than min quantity";
        }
        return "";
      
      case "reorderPoint":
        if (value && newItem.maxQuantity && (value < newItem.minQuantity || value > newItem.maxQuantity)) {
          return "Reorder point must be between min and max quantity";
        }
        return "";
      
      case "reorderQuantity":
        if (value && value <= 0) return "Reorder quantity must be greater than 0";
        return "";
      
      case "supplierContact":
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return "Invalid email format";
        }
        return "";
      
      case "unitOfMeasure":
        // Unit of Measure is now optional
        return "";
      
      case "packSize":
        if (value !== undefined && value <= 0) return "Pack size must be a positive number";
        return "";
      
      case "cartonQuantity":
        if (value !== undefined && value <= 0) return "Carton quantity must be a positive number";
        return "";
      
      case "weightPerUnit":
        if (value !== undefined && value <= 0) return "Weight must be a positive number";
        return "";
      
      case "dimensionLength":
      case "dimensionWidth":
      case "dimensionHeight":
        if (value !== undefined && value <= 0) return "Dimension must be a positive number";
        // Check if all dimensions are provided if any one is provided
        const { dimensionLength, dimensionWidth, dimensionHeight } = newItem;
        const hasDimensions = dimensionLength || dimensionWidth || dimensionHeight;
        if (hasDimensions && (!dimensionLength || !dimensionWidth || !dimensionHeight)) {
          return "All three dimensions (L, W, H) are required if any dimension is provided";
        }
        return "";
      
      default:
        return "";
    }
  };

  // Handle field change
  const handleFieldChange = (field: keyof StockItem, value: any) => {
    const updatedItem = { ...newItem, [field]: value };
    
    // Calculate derived values
    if (field === "physicalQuantity" || field === "unitPrice") {
      updatedItem.value = updatedItem.physicalQuantity * updatedItem.unitPrice;
    }
    
    if (field === "physicalQuantity") {
      updatedItem.freeQuantity = updatedItem.physicalQuantity - updatedItem.allocatedQuantity;
    }
    
    if (field === "lastCostPrice" && !newItem.averageCost) {
      updatedItem.averageCost = value;
    }
    
    // Calculate cubic volume when dimensions or dimension unit changes
    if (field === "dimensionLength" || field === "dimensionWidth" || field === "dimensionHeight" || field === "dimensionUnit") {
      updatedItem.cubicVolume = calculateCubicVolume(
        updatedItem.dimensionLength,
        updatedItem.dimensionWidth,
        updatedItem.dimensionHeight,
        updatedItem.dimensionUnit
      );
    }
    
    setNewItem(updatedItem);
    setHasUnsavedChanges(true);
    
    // Mark field as touched
    setTouchedFields(prev => new Set(prev).add(field));
    
    // Validate field and dependent fields
    const newErrors: ValidationErrors = {};
    newErrors[field] = validateField(field, value);
    
    // Re-validate dependent fields when their dependencies change
    // maxQuantity depends on minQuantity
    if (field === "minQuantity" && touchedFields.has("maxQuantity")) {
      newErrors["maxQuantity"] = updatedItem.maxQuantity && updatedItem.maxQuantity <= updatedItem.minQuantity
        ? "Max quantity must be greater than min quantity"
        : "";
    }
    
    // reorderPoint depends on minQuantity and maxQuantity
    if ((field === "minQuantity" || field === "maxQuantity") && touchedFields.has("reorderPoint")) {
      if (updatedItem.reorderPoint && updatedItem.maxQuantity && 
          (updatedItem.reorderPoint < updatedItem.minQuantity || updatedItem.reorderPoint > updatedItem.maxQuantity)) {
        newErrors["reorderPoint"] = "Reorder point must be between min and max quantity";
      } else {
        newErrors["reorderPoint"] = "";
      }
    }
    
    // Dimension fields depend on each other (all three required if any is provided)
    if (field === "dimensionLength" || field === "dimensionWidth" || field === "dimensionHeight") {
      const hasDimensions = updatedItem.dimensionLength || updatedItem.dimensionWidth || updatedItem.dimensionHeight;
      const allDimensionsProvided = updatedItem.dimensionLength && updatedItem.dimensionWidth && updatedItem.dimensionHeight;
      
      // Re-validate all dimension fields if any has been touched
      if (touchedFields.has("dimensionLength")) {
        newErrors["dimensionLength"] = (hasDimensions && !allDimensionsProvided) 
          ? "All three dimensions (L, W, H) are required if any dimension is provided" 
          : (updatedItem.dimensionLength !== undefined && updatedItem.dimensionLength <= 0 ? "Dimension must be a positive number" : "");
      }
      if (touchedFields.has("dimensionWidth")) {
        newErrors["dimensionWidth"] = (hasDimensions && !allDimensionsProvided) 
          ? "All three dimensions (L, W, H) are required if any dimension is provided" 
          : (updatedItem.dimensionWidth !== undefined && updatedItem.dimensionWidth <= 0 ? "Dimension must be a positive number" : "");
      }
      if (touchedFields.has("dimensionHeight")) {
        newErrors["dimensionHeight"] = (hasDimensions && !allDimensionsProvided) 
          ? "All three dimensions (L, W, H) are required if any dimension is provided" 
          : (updatedItem.dimensionHeight !== undefined && updatedItem.dimensionHeight <= 0 ? "Dimension must be a positive number" : "");
      }
    }
    
    setValidationErrors(prev => ({
      ...prev,
      ...newErrors
    }));
  };

  // Handle field blur
  const handleFieldBlur = (field: keyof StockItem) => {
    setTouchedFields(prev => new Set(prev).add(field));
    const error = validateField(field, newItem[field]);
    setValidationErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  // Validate all fields
  const validateAllFields = (): boolean => {
    const errors: ValidationErrors = {};
    const requiredFields = ["code", "description", "type"];
    
    requiredFields.forEach(field => {
      const error = validateField(field, newItem[field as keyof StockItem]);
      if (error) errors[field] = error;
    });
    
    // Validate other fields that have values
    Object.keys(newItem).forEach(field => {
      if (!requiredFields.includes(field) && newItem[field as keyof StockItem]) {
        const error = validateField(field, newItem[field as keyof StockItem]);
        if (error) errors[field] = error;
      }
    });
    
    setValidationErrors(errors);
    
    // Mark all fields as touched
    setTouchedFields(new Set(Object.keys(newItem)));
    
    return Object.keys(errors).length === 0;
  };

  // Handle image upload
  const handleImageUpload = (file: File) => {
    if (file && (file.type === "image/png" || file.type === "image/jpeg")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handleFieldChange("image", reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      toast.error("Please upload a PNG or JPEG image");
    }
  };

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  // Handle drag events
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  // Handle image removal
  const handleRemoveImage = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the upload zone click
    handleFieldChange("image", "");
  };

  // Handle save
  const handleSave = async () => {
    if (!validateAllFields()) {
      // Find first tab with error
      const errorFields = Object.keys(validationErrors);
      if (errorFields.length > 0) {
        const errorCount = errorFields.length;
        toast.error(`Please fix ${errorCount} error${errorCount > 1 ? 's' : ''} before saving`);
        
        // Navigate to first tab with error
        if (errorFields.some(f => ["code", "description", "category", "location", "barcode", "weight", "binNumber"].includes(f))) {
          setActiveTab("details");
        } else if (errorFields.some(f => ["physicalQuantity", "minQuantity", "maxQuantity", "reorderPoint", "reorderQuantity"].includes(f))) {
          setActiveTab("quantities");
        } else if (errorFields.some(f => ["unitPrice", "lastCostPrice"].includes(f))) {
          setActiveTab("pricing");
        } else if (errorFields.some(f => ["supplierContact"].includes(f))) {
          setActiveTab("supplier");
        }
      }
      return;
    }
    
    setIsSaving(true);
    
    try {
      // Create final item with calculated values
      const finalItem: StockItem = {
        ...newItem,
        value: newItem.physicalQuantity * newItem.unitPrice,
        freeQuantity: newItem.physicalQuantity - newItem.allocatedQuantity,
        lastMovementDate: new Date(),
      };

            if (isLive) {
              // Live mode: Call the actual GraphQL mutation
              const result = await fetchGraphQL<{
                stockItems?: {
                  stockItemsRecords?: {
                    create?: {
                      __typename?: string;
                      id?: number;
                    };
                  };
                };
              }>(CREATE_STOCK_ITEM_MUTATION, {
                stockItem: mapToBackendInput(finalItem),
              });

              if (result.errors && result.errors.length > 0) {
                throw new Error(result.errors[0].message);
              }

              const createResult = result.data?.stockItems?.stockItemsRecords?.create;
        const typename = createResult?.__typename as string | undefined;
        
        // Check if we got a successful IntEntityId result
        if (typename === "IntEntityId" && createResult?.id) {
          finalItem.stockItemID = createResult.id;
          toast.success(`Stock item created successfully (ID: ${createResult.id})`);
        } else if (typename) {
          // We got a non-success union member (validation failure, etc.)
          throw new Error("Failed to create stock item - validation or server error");
        } else {
          // No result at all
          throw new Error("Failed to create stock item - no response from server");
        }
      } else {
        // Demo mode: Simulate API call
        await new Promise(resolve => setTimeout(resolve, 500));
        toast.success("Stock item created successfully (Demo Mode)");
      }
      
      // Clear draft
      localStorage.removeItem("stockItemDraft");
      
      // Call callback if provided
      onItemCreated?.(finalItem);
      
      // Navigate back to stock items
      onNavigate("stock-items");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to create stock item";
      toast.error(`Failed to create stock item: ${message}`);
      console.error("Error creating stock item:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelDialog(true);
    } else {
      onNavigate("stock-items");
    }
  };



  // Get error count
  const errorCount = Object.values(validationErrors).filter(e => e !== "").length;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Create New Stock Item</h1>
            {isDemo ? (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                Demo Mode
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                Live Mode
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-sm ${isDemo ? "text-muted-foreground" : "font-medium"}`}>Demo</span>
            <Switch
              checked={isLive}
              onCheckedChange={(checked) => setMode(checked ? "live" : "demo")}
            />
            <span className={`text-sm ${isLive ? "font-medium" : "text-muted-foreground"}`}>Live</span>
          </div>
        </div>
        <p className="text-muted-foreground mt-2">
          Add a new item to your stock inventory
          {isLive && " - Changes will be saved to the backend"}
        </p>
      </div>

      {/* Error Summary */}
      {errorCount > 0 && touchedFields.size > 0 && (
        <Card className="p-4 border-destructive bg-destructive/5">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
            <div>
              <p className="font-medium text-destructive">
                Please fix {errorCount} error{errorCount > 1 ? 's' : ''} before saving
              </p>
              <ul className="mt-2 space-y-1 text-sm text-destructive/80">
                {Object.entries(validationErrors).map(([field, error]) => 
                  error && (
                    <li key={field}>• {error}</li>
                  )
                )}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {/* Form */}
      <Card className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="details" className="relative">
              Details
              {Object.keys(validationErrors).some(f => ["code", "description", "category"].includes(f)) && (
                <span className="absolute -top-1 -right-1 h-2 w-2 bg-destructive rounded-full" />
              )}
            </TabsTrigger>
            <TabsTrigger value="settings" className="relative">
              Settings
              {Object.keys(validationErrors).some(f => ["type"].includes(f)) && (
                <span className="absolute -top-1 -right-1 h-2 w-2 bg-destructive rounded-full" />
              )}
            </TabsTrigger>
            <TabsTrigger value="quantities" className="relative">
              Quantities
              {Object.keys(validationErrors).some(f => ["physicalQuantity", "minQuantity", "maxQuantity", "reorderPoint", "reorderQuantity"].includes(f)) && (
                <span className="absolute -top-1 -right-1 h-2 w-2 bg-destructive rounded-full" />
              )}
            </TabsTrigger>
            <TabsTrigger value="pricing" className="relative">
              Pricing
              {Object.keys(validationErrors).some(f => ["unitPrice", "lastCostPrice"].includes(f)) && (
                <span className="absolute -top-1 -right-1 h-2 w-2 bg-destructive rounded-full" />
              )}
            </TabsTrigger>
            <TabsTrigger value="analysis">Analysis</TabsTrigger>
            <TabsTrigger value="supplier" className="relative">
              Supplier
              {Object.keys(validationErrors).some(f => ["supplierContact"].includes(f)) && (
                <span className="absolute -top-1 -right-1 h-2 w-2 bg-destructive rounded-full" />
              )}
            </TabsTrigger>
            <TabsTrigger value="intrastat" className="relative">
              Intrastat
              {Object.keys(validationErrors).some(f => ["commodityCode", "countryOfOrigin"].includes(f)) && (
                <span className="absolute -top-1 -right-1 h-2 w-2 bg-destructive rounded-full" />
              )}
            </TabsTrigger>
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details" className="space-y-6 mt-6">
            {/* Image Upload */}
            <div className="space-y-2">
              <Label>Product Image</Label>
              <div className="relative">
                <div
                  className={`border-2 border-dashed rounded-lg p-4 transition-colors cursor-pointer ${
                    isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-muted-foreground/50"
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                  {newItem.image ? (
                    <div className="flex items-center justify-center">
                      <img src={newItem.image} alt="Product" className="h-48 w-48 object-cover rounded" />
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <Upload className="h-12 w-12 text-muted-foreground mb-4" />
                      <p className="text-sm text-muted-foreground">
                        Click to upload or drag image here
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        PNG or JPEG (max 5MB)
                      </p>
                    </div>
                  )}
                </div>
                {/* Remove Image Button */}
                {newItem.image && (
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2 h-8 w-8 rounded-full p-0 shadow-md"
                    onClick={handleRemoveImage}
                    title="Remove image"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Stock Code */}
            <div className="space-y-2">
              <Label htmlFor="code" className="flex items-center gap-1">
                Stock Code <span className="text-destructive">*</span>
              </Label>
              <Input
                id="code"
                value={newItem.code}
                onChange={(e) => handleFieldChange("code", e.target.value)}
                onBlur={() => handleFieldBlur("code")}
                placeholder="Enter stock code"
                maxLength={25}
                className={validationErrors.code && touchedFields.has("code") ? "border-destructive" : ""}
              />
              {validationErrors.code && touchedFields.has("code") && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <X className="h-3 w-3" />
                  {validationErrors.code}
                </p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description" className="flex items-center gap-1">
                Description <span className="text-destructive">*</span>
              </Label>
              <Input
                id="description"
                value={newItem.description}
                onChange={(e) => handleFieldChange("description", e.target.value)}
                onBlur={() => handleFieldBlur("description")}
                placeholder="Enter description"
                maxLength={200}
                className={validationErrors.description && touchedFields.has("description") ? "border-destructive" : ""}
              />
              {validationErrors.description && touchedFields.has("description") && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <X className="h-3 w-3" />
                  {validationErrors.description}
                </p>
              )}
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category" className="flex items-center gap-1">
                Category
              </Label>
              <Select
                value={newItem.category}
                onValueChange={(value) => handleFieldChange("category", value)}
              >
                <SelectTrigger 
                  id="category"
                  className={validationErrors.category && touchedFields.has("category") ? "border-destructive" : ""}
                >
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Electronics">Electronics</SelectItem>
                  <SelectItem value="Furniture">Furniture</SelectItem>
                  <SelectItem value="Office Supplies">Office Supplies</SelectItem>
                  <SelectItem value="Raw Materials">Raw Materials</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              {validationErrors.category && touchedFields.has("category") && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <X className="h-3 w-3" />
                  {validationErrors.category}
                </p>
              )}
            </div>

            {/* Unit of Measure */}
            <div className="space-y-2">
              <Label htmlFor="unitOfMeasure" className="flex items-center gap-1">
                Unit of Measure
              </Label>
              <Select
                value={newItem.unitOfMeasure}
                onValueChange={(value: "Each" | "Box/Carton" | "Kg" | "g" | "Litre" | "Metre" | "Pallet") => handleFieldChange("unitOfMeasure", value)}
              >
                <SelectTrigger 
                  id="unitOfMeasure"
                  className={validationErrors.unitOfMeasure && touchedFields.has("unitOfMeasure") ? "border-destructive" : ""}
                >
                  <SelectValue placeholder="Select unit of measure" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Each">Each</SelectItem>
                  <SelectItem value="Box/Carton">Box/Carton</SelectItem>
                  <SelectItem value="Kg">Kg (Kilogram)</SelectItem>
                  <SelectItem value="g">g (Gram)</SelectItem>
                  <SelectItem value="Litre">Litre</SelectItem>
                  <SelectItem value="Metre">Metre</SelectItem>
                  <SelectItem value="Pallet">Pallet</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Info className="h-3 w-3" />
                This is how you buy, sell & count this item
              </p>
              {validationErrors.unitOfMeasure && touchedFields.has("unitOfMeasure") && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <X className="h-3 w-3" />
                  {validationErrors.unitOfMeasure}
                </p>
              )}
            </div>

            {/* Packaging Information - Collapsible Section */}
            <Collapsible open={isPackagingOpen} onOpenChange={setIsPackagingOpen}>
              <Card className="border-2">
                <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-muted-foreground" />
                    <div className="text-left">
                      <h3 className="font-medium">Packaging Information</h3>
                      <p className="text-xs text-muted-foreground">(Optional)</p>
                    </div>
                  </div>
                  <ChevronRight className={`h-5 w-5 text-muted-foreground transition-transform ${isPackagingOpen ? "rotate-90" : ""}`} />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="p-4 pt-0 space-y-4 border-t">
                    {/* Pack Size */}
                    <div className="space-y-2">
                      <Label htmlFor="packSize">Pack Size</Label>
                      <div className="flex gap-2 items-center">
                        <Input
                          id="packSize"
                          type="number"
                          value={newItem.packSize || ""}
                          onChange={(e) => handleFieldChange("packSize", e.target.value ? parseFloat(e.target.value) : undefined)}
                          onBlur={() => handleFieldBlur("packSize")}
                          placeholder="e.g., 6"
                          className={`flex-1 ${validationErrors.packSize && touchedFields.has("packSize") ? "border-destructive" : ""}`}
                          step="1"
                          min="0"
                        />
                        <span className="text-sm text-muted-foreground whitespace-nowrap">units per pack</span>
                      </div>
                      {validationErrors.packSize && touchedFields.has("packSize") && (
                        <p className="text-sm text-destructive flex items-center gap-1">
                          <X className="h-3 w-3" />
                          {validationErrors.packSize}
                        </p>
                      )}
                    </div>

                    {/* Carton Quantity */}
                    <div className="space-y-2">
                      <Label htmlFor="cartonQuantity">Carton Quantity</Label>
                      <div className="flex gap-2 items-center">
                        <Input
                          id="cartonQuantity"
                          type="number"
                          value={newItem.cartonQuantity || ""}
                          onChange={(e) => handleFieldChange("cartonQuantity", e.target.value ? parseFloat(e.target.value) : undefined)}
                          onBlur={() => handleFieldBlur("cartonQuantity")}
                          placeholder="e.g., 24"
                          className={`flex-1 ${validationErrors.cartonQuantity && touchedFields.has("cartonQuantity") ? "border-destructive" : ""}`}
                          step="1"
                          min="0"
                        />
                        <span className="text-sm text-muted-foreground whitespace-nowrap">units per carton</span>
                      </div>
                      {validationErrors.cartonQuantity && touchedFields.has("cartonQuantity") && (
                        <p className="text-sm text-destructive flex items-center gap-1">
                          <X className="h-3 w-3" />
                          {validationErrors.cartonQuantity}
                        </p>
                      )}
                    </div>

                    {/* Weight per Unit */}
                    <div className="space-y-2">
                      <Label htmlFor="weightPerUnit">Weight per Unit</Label>
                      <div className="flex gap-2">
                        <Input
                          id="weightPerUnit"
                          type="number"
                          value={newItem.weightPerUnit || ""}
                          onChange={(e) => handleFieldChange("weightPerUnit", e.target.value ? parseFloat(e.target.value) : undefined)}
                          onBlur={() => handleFieldBlur("weightPerUnit")}
                          placeholder="Enter weight"
                          className={`flex-1 ${validationErrors.weightPerUnit && touchedFields.has("weightPerUnit") ? "border-destructive" : ""}`}
                          step="0.01"
                          min="0"
                        />
                        <Select
                          value={newItem.weightPerUnitUnit}
                          onValueChange={(value: "kg" | "g" | "lbs") => handleFieldChange("weightPerUnitUnit", value)}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="kg">kg</SelectItem>
                            <SelectItem value="g">g</SelectItem>
                            <SelectItem value="lbs">lbs</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {validationErrors.weightPerUnit && touchedFields.has("weightPerUnit") && (
                        <p className="text-sm text-destructive flex items-center gap-1">
                          <X className="h-3 w-3" />
                          {validationErrors.weightPerUnit}
                        </p>
                      )}
                    </div>

                    {/* Dimensions */}
                    <div className="space-y-2">
                      <Label>Dimensions (L × W × H)</Label>
                      <div className="flex gap-2 items-center">
                        <Input
                          type="number"
                          value={newItem.dimensionLength || ""}
                          onChange={(e) => handleFieldChange("dimensionLength", e.target.value ? parseFloat(e.target.value) : undefined)}
                          onBlur={() => handleFieldBlur("dimensionLength")}
                          placeholder="Length"
                          className={`flex-1 ${validationErrors.dimensionLength && touchedFields.has("dimensionLength") ? "border-destructive" : ""}`}
                          step="0.01"
                          min="0"
                        />
                        <span className="text-muted-foreground">×</span>
                        <Input
                          type="number"
                          value={newItem.dimensionWidth || ""}
                          onChange={(e) => handleFieldChange("dimensionWidth", e.target.value ? parseFloat(e.target.value) : undefined)}
                          onBlur={() => handleFieldBlur("dimensionWidth")}
                          placeholder="Width"
                          className={`flex-1 ${validationErrors.dimensionWidth && touchedFields.has("dimensionWidth") ? "border-destructive" : ""}`}
                          step="0.01"
                          min="0"
                        />
                        <span className="text-muted-foreground">×</span>
                        <Input
                          type="number"
                          value={newItem.dimensionHeight || ""}
                          onChange={(e) => handleFieldChange("dimensionHeight", e.target.value ? parseFloat(e.target.value) : undefined)}
                          onBlur={() => handleFieldBlur("dimensionHeight")}
                          placeholder="Height"
                          className={`flex-1 ${validationErrors.dimensionHeight && touchedFields.has("dimensionHeight") ? "border-destructive" : ""}`}
                          step="0.01"
                          min="0"
                        />
                        <Select
                          value={newItem.dimensionUnit}
                          onValueChange={(value: "cm" | "m" | "in" | "ft") => handleFieldChange("dimensionUnit", value)}
                        >
                          <SelectTrigger className="w-20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cm">cm</SelectItem>
                            <SelectItem value="m">m</SelectItem>
                            <SelectItem value="in">in</SelectItem>
                            <SelectItem value="ft">ft</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {(validationErrors.dimensionLength || validationErrors.dimensionWidth || validationErrors.dimensionHeight) && 
                       (touchedFields.has("dimensionLength") || touchedFields.has("dimensionWidth") || touchedFields.has("dimensionHeight")) && (
                        <p className="text-sm text-destructive flex items-center gap-1">
                          <X className="h-3 w-3" />
                          {validationErrors.dimensionLength || validationErrors.dimensionWidth || validationErrors.dimensionHeight}
                        </p>
                      )}
                    </div>

                    {/* Cubic Volume - Read-only */}
                    <div className="space-y-2">
                      <Label>Cubic Volume (Auto-calculated)</Label>
                      <div className="p-3 bg-muted/50 rounded-md border">
                        <p className="text-sm font-mono">
                          {formatCubicVolume(newItem.cubicVolume)}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Automatically calculated from dimensions
                      </p>
                    </div>
                  </div>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Location */}
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Select
                value={newItem.location}
                onValueChange={(value) => handleFieldChange("location", value)}
              >
                <SelectTrigger id="location">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Warehouse A">Warehouse A</SelectItem>
                  <SelectItem value="Warehouse B">Warehouse B</SelectItem>
                  <SelectItem value="Retail Store">Retail Store</SelectItem>
                  <SelectItem value="Storage Room">Storage Room</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Leave blank if not using locations</p>
            </div>

            {/* Active Status */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="status">Active Status</Label>
                <p className="text-xs text-muted-foreground">Item is available for use</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Inactive</span>
                <Switch
                  id="status"
                  checked={!newItem.inactive}
                  onCheckedChange={(checked) => handleFieldChange("inactive", !checked)}
                />
                <span className="text-sm text-muted-foreground">Active</span>
              </div>
            </div>

            {/* Weight */}
            <div className="space-y-2">
              <Label htmlFor="weight">Weight</Label>
              <div className="flex gap-2">
                <Input
                  id="weight"
                  type="number"
                  value={newItem.weight || ""}
                  onChange={(e) => handleFieldChange("weight", e.target.value ? parseFloat(e.target.value) : undefined)}
                  placeholder="Enter weight"
                  className="flex-1"
                  step="0.01"
                  min="0"
                />
                <Select
                  value={newItem.weightUnit}
                  onValueChange={(value: "kg" | "g" | "lbs") => handleFieldChange("weightUnit", value)}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="g">g</SelectItem>
                    <SelectItem value="lbs">lbs</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Barcode */}
            <div className="space-y-2">
              <Label htmlFor="barcode">Barcode</Label>
              <Input
                id="barcode"
                value={newItem.barcode}
                onChange={(e) => handleFieldChange("barcode", e.target.value)}
                onBlur={() => handleFieldBlur("barcode")}
                placeholder="Enter barcode (8-13 digits)"
                maxLength={13}
                className={validationErrors.barcode && touchedFields.has("barcode") ? "border-destructive" : ""}
              />
              {validationErrors.barcode && touchedFields.has("barcode") && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <X className="h-3 w-3" />
                  {validationErrors.barcode}
                </p>
              )}
            </div>

            {/* Bin Number */}
            <div className="space-y-2">
              <Label htmlFor="binNumber">Bin Number</Label>
              <Input
                id="binNumber"
                value={newItem.binNumber}
                onChange={(e) => handleFieldChange("binNumber", e.target.value)}
                placeholder="Enter bin number"
                maxLength={20}
              />
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-6">
            <StockItemSettingsTab
              item={newItem}
              isEditMode={true}
              onItemChange={(updatedItem) => {
                setNewItem(updatedItem);
                setHasUnsavedChanges(true);
              }}
            />
          </TabsContent>

          {/* Quantities Tab */}
          <TabsContent value="quantities" className="mt-6">
            <StockItemQuantitiesTab
              item={newItem}
              isEditMode={true}
              onItemChange={(updatedItem) => {
                setNewItem(updatedItem);
                setHasUnsavedChanges(true);
              }}
              validationErrors={validationErrors}
              touchedFields={touchedFields}
              onFieldBlur={handleFieldBlur}
            />
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing" className="mt-6">
            <StockItemPricingTab
              item={newItem}
              isEditMode={true}
              onItemChange={(updatedItem) => {
                setNewItem(updatedItem);
                setHasUnsavedChanges(true);
              }}
              validationErrors={validationErrors}
              touchedFields={touchedFields}
              onFieldBlur={handleFieldBlur}
            />
          </TabsContent>

          {/* Analysis Tab */}
          <TabsContent value="analysis" className="mt-6">
            <StockItemAnalysisTab
              item={newItem}
              isEditMode={true}
              onItemChange={(updatedItem) => {
                setNewItem(updatedItem);
                setHasUnsavedChanges(true);
              }}
            />
          </TabsContent>

          {/* Supplier Tab */}
          <TabsContent value="supplier" className="mt-6">
            <StockItemSupplierTab
              item={newItem}
              isEditMode={true}
              onItemChange={(updatedItem) => {
                setNewItem(updatedItem);
                setHasUnsavedChanges(true);
              }}
              validationErrors={validationErrors}
              touchedFields={touchedFields}
              onFieldBlur={handleFieldBlur}
            />
          </TabsContent>

          {/* Intrastat Tab */}
          <TabsContent value="intrastat" className="mt-6">
            <StockItemIntrastatTab
              item={newItem}
              onChange={(field, value) => {
                setNewItem({ ...newItem, [field]: value });
                setHasUnsavedChanges(true);
              }}
              onBlur={handleFieldBlur}
              validationErrors={validationErrors}
              touchedFields={touchedFields}
            />
          </TabsContent>
        </Tabs>
      </Card>

      {/* Footer Actions */}
      <div className="sticky bottom-0 bg-muted/50 backdrop-blur-sm border-t p-4 -mx-6 -mb-6 flex items-center justify-end gap-3">
        <Button
          variant="outline"
          onClick={handleCancel}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaving || (errorCount > 0 && touchedFields.size > 0)}
        >
          {isSaving ? "Saving..." : "Save Stock Item"}
        </Button>
      </div>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard New Stock Item?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave without saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Editing</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              localStorage.removeItem("stockItemDraft");
              onNavigate("stock-items");
            }}>
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
